// Main tab (docs/06 §2): Favorites (speed-dials, managed from contact detail)
// and Recents (the device-local call log with missed-call badges + call-back —
// call history never comes from the server). The degraded-network rules from
// docs/06 §5 that apply outside a call live here too: the subtle offline dot
// and the "Trying to reach Merkury…" manual-retry banner.
//
// Offline-first: friends/blocks state boots from the local cache (last
// successful fetch), so a cold start with the backend unreachable still shows
// everything — the network only ever updates the view. Presence from the cache
// is already degraded to offline (serverCache.ts).

import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Friend, PresenceStatus } from '@merkury/protocol';
import type { SignalingStatus } from '@merkury/brain/call/types.js';
import {
  getCachedBlocks,
  getCachedFriends,
  setCachedBlocks,
  setCachedFriends,
} from '../../storage/serverCache.js';
import { markAllMissedSeen } from '../../storage/kvStores.js';
import { useT, type MessageKey } from '../../i18n/index.js';
import { presenceOf } from '@merkury/brain/api/liveEvents.js';
import { useScreens } from '../ScreensContext.js';
import { useStore } from '../ui/useStore.js';
import { useFavorites } from '../ui/useFavorites.js';
import { useCallLog } from '../ui/useCallLog.js';
import { useDialer } from '../ui/useDialer.js';
import { useServerReload } from '../ui/useServerReload.js';
import { describeCall, formatTimestamp } from '../ui/callFormat.js';
import { displayNameFor, effectiveName } from '../ui/peerName.js';
import { useAliases } from '../ui/useAliases.js';
import { FsiAttentionBanner } from '../ui/FsiAttentionBanner.js';
import { InstallHint } from '../ui/InstallHint.js';
import { PeerAvatar } from '../ui/PeerAvatar.js';
import { EmptyState } from '../ui/EmptyState.js';
import { PhoneGlyph } from '../ui/icons.js';

// Signaling → status-dot colour + label key (docs/06 §5): green connected,
// yellow while the socket is racing/reconnecting, red when down or revoked.
const SIGNALING_DOT: Record<SignalingStatus, { color: string; labelKey: MessageKey }> = {
  idle: { color: 'bg-bad', labelKey: 'main.signalingDown' },
  connecting: { color: 'bg-warn', labelKey: 'main.signalingConnecting' },
  connected: { color: 'bg-ok', labelKey: 'main.signalingConnected' },
  reconnecting: { color: 'bg-warn', labelKey: 'main.signalingReconnecting' },
  revoked: { color: 'bg-bad', labelKey: 'main.signalingDown' },
};

export function MainScreen(): ReactNode {
  const t = useT();
  const { api, engine, liveEvents } = useScreens();
  const navigate = useNavigate();

  const engineState = useStore(engine.state);
  const live = useStore(liveEvents.state);
  const favorites = useFavorites();
  const calls = useCallLog();
  const aliases = useAliases();
  const { notice, dial } = useDialer();

  const [friends, setFriends] = useState<Friend[]>(() => getCachedFriends()?.data ?? []);
  const [blocked, setBlocked] = useState<string[]>(() => getCachedBlocks()?.data ?? []);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const friendsRes = await api.listFriends();
      setFriends(friendsRes.friends);
      setCachedFriends(friendsRes.friends);
      setError(null);
    } catch {
      // Offline-tolerant: keep the last known list, no modal (prime directive).
      setError(t('sync.refreshError'));
    }
    // Blocks are only used to hide blocked contacts from Favorites; a failure
    // here must never wipe the screen — degrade to the last known set.
    try {
      const res = await api.listBlocks();
      setBlocked(res.blocked);
      setCachedBlocks(res.blocked);
    } catch {
      // keep last known blocked set
    }
  }, [api, t]);

  useServerReload(reload);

  // Docs/06 §5: "All endpoints unreachable" gets a manual-retry banner with an
  // attempt count. The engine doesn't publish an attempt counter, so Main
  // counts its own transitions into connecting/reconnecting as a proxy.
  const attemptsRef = useRef(0);
  const prevSignalingRef = useRef(engineState.signaling);
  if (prevSignalingRef.current !== engineState.signaling) {
    if (engineState.signaling === 'connecting' || engineState.signaling === 'reconnecting') {
      attemptsRef.current += 1;
    } else if (engineState.signaling === 'connected') {
      attemptsRef.current = 0;
    }
    prevSignalingRef.current = engineState.signaling;
  }
  const unreachable = engineState.signaling === 'reconnecting' && attemptsRef.current > 1;

  const missedUnseen = calls.filter((c) => c.outcome === 'missed' && !c.seen);

  function handleOpenRecents(): void {
    // Local log, local badge: clearing "seen" needs no server round-trip.
    void markAllMissedSeen();
  }

  const presenceFor = (friend: Friend): PresenceStatus =>
    presenceOf(live, friend.userId, friend);

  async function callFriend(friend: Friend): Promise<void> {
    // Yellow (reachable) friends get no offline warning — their phone rings.
    const hint =
      presenceFor(friend) === 'offline'
        ? t('call.offlineHint', { name: effectiveName(friend, aliases) })
        : undefined;
    await dial(friend.userId, hint);
  }

  // Favorites: known friends the user starred, minus anyone currently blocked.
  const favoriteFriends = favorites
    .filter((id) => !blocked.includes(id))
    .map((id) => friends.find((f) => f.userId === id))
    .filter((f): f is Friend => f !== undefined);

  return (
    <main className="mx-auto flex min-h-full w-full max-w-app flex-col gap-5 p-4">
      <header className="flex items-center gap-2 px-1 pt-1">
        <h1 className="text-xl font-semibold tracking-[0.2em]">MERKURY</h1>
        <span
          role="status"
          aria-label={t('main.serverConnection', {
            label: t(SIGNALING_DOT[engineState.signaling].labelKey),
          })}
          title={t('main.serverSignaling')}
          className={`inline-block h-2 w-2 rounded-full ${SIGNALING_DOT[engineState.signaling].color}`}
        />
      </header>
      {/* OEMs reset the full-screen-intent grant on every self-update: surface
          the loss where the user lands after updating, not only in settings. */}
      <FsiAttentionBanner />
      {/* iOS Safari tab / notifications denied: calls can't ring — dismissible
          install/enable hint (renders nothing when pushes work or on native). */}
      <InstallHint />

      {unreachable && (
        <div role="alert" className="rounded-xl bg-surface p-4 text-sm">
          <p className="text-ink">{t('main.trying', { n: attemptsRef.current })}</p>
          <button
            type="button"
            onClick={() => engine.start()}
            className="mt-2 min-h-touch rounded-xl bg-raised px-3 text-sm text-ink"
          >
            {t('main.retryNow')}
          </button>
        </div>
      )}

      {notice && (
        <p role="status" className="text-sm text-warn">
          {notice}
        </p>
      )}
      {error && (
        <p role="status" className="text-sm text-muted">
          {error}
        </p>
      )}

      {/* No favorites → no block at all; starring lives on the contact screen. */}
      {favoriteFriends.length > 0 && (
        <section aria-label={t('main.favorites')} className="flex flex-col gap-2">
          <h2 className="px-1 text-xs font-semibold uppercase tracking-wide text-muted">
            {t('main.favorites')}
          </h2>
          <ul className="flex flex-col gap-1.5">
            {favoriteFriends.map((friend) => {
              const name = effectiveName(friend, aliases);
              return (
                <li key={friend.userId}>
                  <button
                    type="button"
                    aria-label={t('contacts.callAria', { name })}
                    onClick={() => void callFriend(friend)}
                    className="flex min-h-touch w-full items-center gap-3 rounded-2xl bg-surface p-2.5 text-left active:bg-raised"
                  >
                    <PeerAvatar
                      userId={friend.userId}
                      name={name}
                      {...(friend.avatarSeed !== undefined ? { avatarSeed: friend.avatarSeed } : {})}
                      presence={presenceFor(friend)}
                    />
                    <span className="min-w-0 flex-1 truncate font-medium text-ink">{name}</span>
                    <span aria-hidden="true" className="mr-1.5 text-accent">
                      <PhoneGlyph />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section aria-label={t('main.recents')} className="flex flex-col gap-2">
        <button
          type="button"
          onClick={handleOpenRecents}
          className="flex items-center gap-2 px-1 text-left text-xs font-semibold uppercase tracking-wide text-muted"
        >
          {t('main.recents')}
          {missedUnseen.length > 0 && (
            <span
              data-testid="missed-badge"
              aria-label={t('main.missedBadge', { count: missedUnseen.length })}
              className="rounded-full bg-danger px-2 py-0.5 text-xs font-medium text-ink"
            >
              {missedUnseen.length}
            </span>
          )}
        </button>
        {calls.length === 0 && (
          <EmptyState
            icon={<PhoneGlyph />}
            title={t('main.noCalls')}
            help={t('main.noCallsHelp')}
          />
        )}
        <ul className="flex flex-col gap-1.5">
          {calls.map((entry) => {
            const name = displayNameFor(entry.peerUserId, friends, entry.peerName);
            const missed = entry.outcome === 'missed';
            // A blocked peer keeps their name in history — struck through,
            // not replaced with "Unknown caller" (they are known; you blocked them).
            const isBlocked = blocked.includes(entry.peerUserId);
            const seed = friends.find((f) => f.userId === entry.peerUserId)?.avatarSeed;
            return (
              <li
                key={entry.id}
                className="flex items-center rounded-2xl bg-surface"
                data-missed={missed && !entry.seen ? 'true' : undefined}
              >
                <button
                  type="button"
                  aria-label={t('contacts.openAria', { name })}
                  onClick={() => navigate(`/contact/${entry.peerUserId}`)}
                  className="flex min-h-touch min-w-0 flex-1 items-center gap-3 rounded-2xl p-2.5 text-left active:bg-raised"
                >
                  <PeerAvatar userId={entry.peerUserId} name={name} {...(seed !== undefined ? { avatarSeed: seed } : {})} />
                  <span className="min-w-0 flex-1">
                    <span
                      className={`block truncate font-medium ${missed ? 'text-danger' : 'text-ink'} ${isBlocked ? 'line-through' : ''}`}
                    >
                      {name}
                    </span>
                    <span className="block truncate text-xs text-muted">
                      {describeCall(entry)} · {formatTimestamp(entry.startedAt)}
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  aria-label={t('main.callBackAria', { name })}
                  onClick={() => void dial(entry.peerUserId)}
                  className="mr-1.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-accent active:bg-raised"
                >
                  <PhoneGlyph />
                </button>
              </li>
            );
          })}
        </ul>
      </section>
    </main>
  );
}
